import React from 'react';
import { Box, Typography } from '@mui/material';
import logoUrl from '@/assets/runnrz-logo-square.png';
import { tokens } from '@/theme/theme';
import { getEnvironment } from '@/utils/environment';

type Step = { key: string; label: string };

type Props = {
  steps?: Step[];
  activeStep?: number;
  children: React.ReactNode;
};

/** Mise en page des écrans publics : panneau de marque à gauche, formulaire à droite. */
export default function AuthShell({ steps, activeStep = 0, children }: Props) {
  const env = getEnvironment();
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default' }}>
      <Box
        component="aside"
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: '0 0 44%',
          maxWidth: 620,
          bgcolor: tokens.ink,
          color: '#F6F6F3',
          p: 6,
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          component="svg"
          aria-hidden
          viewBox="0 0 600 600"
          sx={{ position: 'absolute', right: -140, bottom: -120, width: 560, height: 560, opacity: 0.9 }}
        >
          <path d="M60 520 C 160 380, 120 260, 260 220 S 420 260, 470 140 S 560 60, 600 40" fill="none" stroke={tokens.accent} strokeWidth="3" strokeLinecap="round" />
          <path d="M20 560 C 140 440, 90 320, 230 270 S 400 300, 450 190" fill="none" stroke="#3A3935" strokeWidth="2" strokeDasharray="2 8" strokeLinecap="round" />
          <circle cx="60" cy="520" r="7" fill={tokens.accent} />
          <circle cx="470" cy="140" r="5" fill="#F6F6F3" />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative' }}>
          <Box component="img" src={logoUrl} alt="" sx={{ width: 36, height: 36, borderRadius: '9px' }} />
          <Typography sx={{ fontWeight: 600, fontSize: 18, letterSpacing: '-0.01em' }}>
            RunnrZ{' '}
            <Box component="span" sx={{ fontWeight: 400, color: '#A8A6A0' }}>
              Admin
            </Box>
          </Typography>
        </Box>
        <Box sx={{ position: 'relative', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
          <Typography component="p" sx={{ fontSize: 34, lineHeight: 1.15, fontWeight: 600, letterSpacing: '-0.025em' }}>
            Console d'administration
          </Typography>
          <Typography sx={{ fontSize: 15, lineHeight: 1.55, color: '#C9C7C0' }}>
            Utilisateurs, parcours, défis et sponsors. Accès réservé à l'équipe, avec authentification à deux facteurs.
          </Typography>
        </Box>
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, color: '#8C8A84' }}>
          <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: env.color }} />
          Environnement : {env.label}
        </Box>
      </Box>

      <Box
        component="main"
        sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3, py: 6 }}
      >
        <Box sx={{ width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.25 }}>
            <Box component="img" src={logoUrl} alt="" sx={{ width: 32, height: 32, borderRadius: '8px' }} />
            <Typography sx={{ fontWeight: 600 }}>RunnrZ Admin</Typography>
            <Box
              component="span"
              sx={{ ml: 'auto', fontSize: 11.5, fontWeight: 500, px: 1, py: 0.25, borderRadius: 999, bgcolor: env.bg, color: env.color }}
            >
              {env.label}
            </Box>
          </Box>

          {steps && steps.length > 1 && (
            <Box component="ol" aria-label="Étapes" sx={{ display: 'flex', alignItems: 'center', gap: 1, m: 0, p: 0, listStyle: 'none', fontSize: 12, color: 'text.secondary' }}>
              {steps.map((step, index) => (
                <React.Fragment key={step.key}>
                  {index > 0 && <Box component="li" aria-hidden sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />}
                  <Box
                    component="li"
                    aria-current={index === activeStep ? 'step' : undefined}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, color: index === activeStep ? 'text.primary' : undefined, fontWeight: index === activeStep ? 500 : 400 }}
                  >
                    <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: index <= activeStep ? tokens.accentStrong : tokens.lineStrong }} />
                    {step.label}
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          )}

          {children}
        </Box>
      </Box>
    </Box>
  );
}
